import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, formatBRL } from "../_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, DollarSign, Activity, Database, MessageCircle, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const PLAN_COLORS = { starter: "#94A3B8", growth: "#F05A3F", pro: "#34D399" };

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setData(r.data)); }, []);

  if (!data) return <Skeleton className="h-96" />;

  const planData = Object.entries(data.plans).map(([k, v]) => ({ name: k, value: v }));

  return (
    <Page title="Painel administrativo" subtitle="Visão consolidada da plataforma SmartStockAI.">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI testid="admin-kpi-mrr" icon={DollarSign} label="MRR estimado" value={formatBRL(data.mrr)} hint={`ARR ${formatBRL(data.arr)}`} accent />
        <KPI testid="admin-kpi-users" icon={Users} label="Usuários totais" value={data.total_users} hint={`${data.active_users} ativos`} />
        <KPI testid="admin-kpi-products" icon={Database} label="Produtos cadastrados" value={data.product_count.toLocaleString("pt-BR")} hint={`${data.sales_count.toLocaleString("pt-BR")} vendas`} />
        <KPI testid="admin-kpi-activity" icon={Activity} label="Logins (14d)" value={data.recent_logins_14d} hint={`${data.chat_count} mensagens IA`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold mb-4">Cadastros (últimos 14 dias)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.signups_14d}>
              <CartesianGrid stroke="#1A2B47" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#0B1320", border: "1px solid #1A2B47", borderRadius: 12 }} />
              <Bar dataKey="count" fill="#F05A3F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold mb-4">Distribuição de planos</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={planData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4}>
                {planData.map((p) => <Cell key={p.name} fill={PLAN_COLORS[p.name]} />)}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "#0B1320", border: "1px solid #1A2B47", borderRadius: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-primary" /> Uso de LLM (14d)
          </h3>
          {data.llm_usage_14d.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhuma chamada nos últimos 14 dias.</div>
          ) : (
            <div className="space-y-3">
              {data.llm_usage_14d.map((u) => (
                <div key={u.provider} className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/40">
                  <div className="font-mono text-sm">{u.provider}</div>
                  <div className="font-display text-2xl font-bold text-primary">{u.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Saúde da plataforma
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Mini label="Lojas" value={data.store_count} />
            <Mini label="Produtos" value={data.product_count} />
            <Mini label="Vendas" value={data.sales_count} />
            <Mini label="Mensagens IA" value={data.chat_count} />
          </div>
        </div>
      </div>
    </Page>
  );
}

const KPI = ({ icon: Icon, label, value, hint, accent, testid }) => (
  <div data-testid={testid} className={`p-6 rounded-xl border bg-card ${accent ? "border-primary/40 glow-coral" : "border-border"}`}>
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">{label}<Icon className="w-4 h-4" /></div>
    <div className={`font-display text-3xl font-bold mt-3 ${accent ? "text-primary" : ""}`}>{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
  </div>
);

const Mini = ({ label, value }) => (
  <div className="p-4 rounded-lg border border-border bg-secondary/40">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-display text-2xl font-bold mt-1">{value.toLocaleString("pt-BR")}</div>
  </div>
);
